import api from "../api";
import { GenerateSkuPayload, ProductInfoOnloadResponse, SaveProductSetupPayload } from "@/types/productDevelopment.types";
import { handleApiError } from "@/hooks/useApiErrorHandler";

const { productServiceAPI } = api;

const MT_PI_PS_ONLOAD_PATH = "/productInfo/product-setup/onload"; // My tasks, product setup
const SAVE_PRODUCT_SETUP_PATH = "/productInfo/save/sku/";

// Helper function to extract error message from various error structures
const extractErrorMessage = (error: any): string => {
  if (!error) return 'Unknown error';
  
  // Check various common error structures
  if (error?.response?.data?.error) return error.response.data.error;
  if (error?.response?.data?.message) return error.response.data.message;
  if (error?.message) return error.message;
  if (typeof error === 'string') return error;
  
  return 'An unexpected error occurred';
};

const ProductDevelopmentService = {
  getProductInfoOnload: async (ideaId: string, vendorAssessmentId: string): Promise<ProductInfoOnloadResponse> => {
    try {
      const reqOnload = productServiceAPI.get(MT_PI_PS_ONLOAD_PATH);
      const reqInfo = productServiceAPI.get(`/productInfo/product-setup/info?ideaId=${ideaId}&vendorAssessmentId=${vendorAssessmentId}`);
      
      const results = await Promise.allSettled([reqOnload, reqInfo]);
      const [onLoadResult, infoResult] = results;
      
      // Check if onload API call failed
      if (onLoadResult.status === 'rejected') {
        handleApiError(onLoadResult.reason, 'Failed to load product setup data');
        const errorMessage = extractErrorMessage(onLoadResult.reason);
        console.error("Error loading product setup onload data:", errorMessage);
        throw new Error(`Failed to load product setup data: ${errorMessage}`);
      }
      
      // Check if info API call failed
      if (infoResult.status === 'rejected') {
        handleApiError(infoResult.reason, 'Failed to load product information');
        const errorMessage = extractErrorMessage(infoResult.reason);
        console.error("Error loading product info:", errorMessage);
        throw new Error(`Failed to load product information: ${errorMessage}`);
      }
      
      return {
        onload: onLoadResult.status === 'fulfilled' ? onLoadResult.value.data : null,
        info: infoResult.status === 'fulfilled' ? infoResult.value.data : null
      };
    } 
    catch (error) {
      console.error("Error in getProductInfoOnload:", error);
      throw error;
    }
  },
  generateSku: async (payload: GenerateSkuPayload, ideaId: string) => {
    try {
      const resp = await productServiceAPI.post(`/productInfo/generate/sku/${ideaId}`, payload);
      return resp.data;
    } catch (error) {
      throw error;
    }
  },
  saveProductSetup: async (SaveProductSetupPayload: SaveProductSetupPayload, ideaId: string) => {
    try {
      const resp = await productServiceAPI.put(SAVE_PRODUCT_SETUP_PATH + ideaId ,SaveProductSetupPayload);
      return resp;
    } catch (error) {
      throw error;
    }
  },
};

export default ProductDevelopmentService;
